import { Link, useLocation } from "wouter";
import { useAuth } from "@/hooks/use-auth";
import { useQuery } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import staioIcon from "@/assets/images/staio-icon.png";
import {
  Building2,
  Home,
  FileText,
  LogOut,
  Menu,
  ClipboardList,
  LayoutDashboard,
  Sparkles,
  Users,
  Briefcase,
  CalendarDays,
  DollarSign,
  BarChart3,
  CreditCard,
  LifeBuoy,
  HelpCircle,
  Wallet,
  Globe,
  Radio,
  Banknote,
  ShoppingBag,
  KeyRound,
  SlidersHorizontal,
  Percent,
  UserPlus,
  ChevronDown,
  Shield,
  TrendingUp,
  Lightbulb,
  MessageSquare,
  MessageSquarePlus,
  Zap,
  Receipt,
  Plug,
  Package,
  PenLine,
  Settings,
  ScanLine,
  Network,
  Wind,
  type LucideIcon,
} from "lucide-react";
import { useState, useMemo, useCallback } from "react";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import NotificationBell from "@/components/notification-bell";
import { FeedbackDialog } from "@/components/feedback-dialog";
import LumiGlobal from "@/components/lumi-global";
import LumiCommand from "@/components/lumi-command";
import { apiRequest } from "@/lib/queryClient";
import { useQueryClient } from "@tanstack/react-query";

interface NavItem {
  title: string;
  href: string;
  icon: LucideIcon;
  testId: string;
  featureKey?: string;
  section?: string;
}

const navItems: NavItem[] = [
  { title: "Dashboard", href: "/admin", icon: LayoutDashboard, testId: "nav-dashboard" },
  { title: "Lumi", href: "/admin/lumi", icon: Lightbulb, testId: "nav-lumi", featureKey: "lumi_ai" },
  { title: "Forecast & Analytics", href: "/admin/forecast", icon: TrendingUp, testId: "nav-forecast", featureKey: "forecast" },

  { title: "Buildings", href: "/admin/buildings", icon: Building2, testId: "nav-buildings", featureKey: "buildings_units", section: "Properties" },
  { title: "Apartments", href: "/admin/apartments", icon: Home, testId: "nav-apartments", featureKey: "buildings_units", section: "Properties" },
  { title: "Content Sections", href: "/admin/content", icon: FileText, testId: "nav-content", featureKey: "content_guides", section: "Properties" },
  { title: "Smart Locks", href: "/admin/smart-locks", icon: KeyRound, testId: "nav-smart-locks", featureKey: "smart_locks", section: "Properties" },
  { title: "Custom Fields", href: "/admin/custom-fields", icon: SlidersHorizontal, testId: "nav-custom-fields", featureKey: "custom_fields", section: "Properties" },
  { title: "Inventory", href: "/admin/inventory", icon: Package, testId: "nav-inventory", featureKey: "inventory", section: "Properties" },

  { title: "Reservations", href: "/admin/reservations", icon: CalendarDays, testId: "nav-reservations", featureKey: "reservations", section: "Bookings" },
  { title: "Booking Engine", href: "/admin/booking", icon: Globe, testId: "nav-booking", featureKey: "booking_engine", section: "Bookings" },
  { title: "Channel Manager", href: "/admin/channels", icon: Radio, testId: "nav-channels", featureKey: "channel_manager", section: "Bookings" },
  { title: "Upsells & Add-ons", href: "/admin/upsells", icon: ShoppingBag, testId: "nav-upsells", featureKey: "upsells_addons", section: "Bookings" },
  { title: "Submissions", href: "/admin/submissions", icon: ClipboardList, testId: "nav-submissions", featureKey: "submissions_checkout", section: "Bookings" },

  { title: "CRM", href: "/admin/crm", icon: MessageSquare, testId: "nav-crm", featureKey: "guest_crm", section: "Operations" },
  { title: "Contracts", href: "/admin/contracts", icon: PenLine, testId: "nav-contracts", section: "Operations" },
  { title: "Daily Reports", href: "/admin/daily-reports", icon: FileText, testId: "nav-daily-reports", featureKey: "daily_reports", section: "Operations" },
  { title: "Automation", href: "/admin/automation", icon: Zap, testId: "nav-automation", featureKey: "automation_engine", section: "Operations" },
  { title: "Cleaning Jobs", href: "/admin/cleaning-jobs", icon: Sparkles, testId: "nav-cleaning-jobs", featureKey: "cleaning_jobs", section: "Operations" },
  { title: "Cleaning Inventory", href: "/admin/cleaning-inventory", icon: Package, testId: "nav-cleaning-inventory", featureKey: "cleaning_inventory", section: "Operations" },
  { title: "QA Vision", href: "/admin/qa-settings", icon: ScanLine, testId: "nav-qa-vision", featureKey: "qa_vision", section: "Operations" },
  { title: "Maintenance", href: "/admin/maintenance", icon: Briefcase, testId: "nav-maintenance", featureKey: "maintenance_ops", section: "Operations" },
  { title: "Laundry Jobs", href: "/admin/laundry-jobs", icon: Wind, testId: "nav-laundry-jobs", section: "Operations" },

  { title: "Unit Costs", href: "/admin/unit-costs", icon: Receipt, testId: "nav-unit-costs", featureKey: "unit_costs", section: "Finance" },
  { title: "Finances", href: "/admin/finances", icon: DollarSign, testId: "nav-finances", featureKey: "finance_core", section: "Finance" },
  { title: "Guest Payments", href: "/admin/guest-payments", icon: Banknote, testId: "nav-guest-payments", featureKey: "guest_payments", section: "Finance" },
  { title: "Caja / Cash Box", href: "/admin/caja", icon: Wallet, testId: "nav-caja", featureKey: "cash_box", section: "Finance" },
  { title: "Reports", href: "/admin/reports", icon: BarChart3, testId: "nav-reports", featureKey: "reports_basic", section: "Finance" },
  { title: "PM Invoices", href: "/admin/invoices", icon: FileText, testId: "nav-invoices", featureKey: "billing_invoices", section: "Finance" },
  { title: "Contractor Invoices", href: "/admin/contractor-invoices", icon: ClipboardList, testId: "nav-contractor-invoices", featureKey: "contractor_invoices", section: "Finance" },
  { title: "Tax & 1099", href: "/admin/tax-compliance", icon: Shield, testId: "nav-tax-compliance", featureKey: "tax_compliance", section: "Finance" },
  { title: "Billing", href: "/admin/billing", icon: CreditCard, testId: "nav-billing", featureKey: "billing_invoices", section: "Finance" },
  { title: "Commissions", href: "/admin/commissions", icon: Percent, testId: "nav-commissions", featureKey: "commissions", section: "Finance" },
  { title: "Affiliates", href: "/admin/affiliates", icon: UserPlus, testId: "nav-affiliates", featureKey: "affiliates", section: "Finance" },

  { title: "Organization Settings", href: "/admin/org-settings", icon: Settings, testId: "nav-org-settings", section: "System" },
  { title: "Team", href: "/admin/users", icon: Users, testId: "nav-users", featureKey: "team_management", section: "System" },
  { title: "Cross-Org Contracts", href: "/admin/cross-org", icon: Network, testId: "nav-cross-org", section: "System" },
  { title: "Audit Logs", href: "/admin/audit-logs", icon: Shield, testId: "nav-audit-logs", section: "System" },
  { title: "Tickets", href: "/admin/tickets", icon: LifeBuoy, testId: "nav-tickets", featureKey: "support_tickets", section: "System" },
  { title: "Integrations", href: "/admin/integrations", icon: Plug, testId: "nav-integrations", featureKey: "integrations_admin", section: "System" },
  { title: "Help & Tutorial", href: "/help", icon: HelpCircle, testId: "nav-help", section: "System" },
];

function ImpersonationBanner({ orgName }: { orgName?: string }) {
  const queryClient = useQueryClient();
  const [, navigate] = useLocation();
  const [stopping, setStopping] = useState(false);

  const handleStop = async () => {
    setStopping(true);
    try {
      await apiRequest("POST", "/api/platform/impersonate/stop", {});
      queryClient.invalidateQueries({ queryKey: ["/api/user"] });
      navigate("/platform");
    } finally {
      setStopping(false);
    }
  };

  return (
    <div className="bg-amber-50 border-b border-amber-200 px-4 py-2 flex items-center justify-between gap-4 shrink-0" data-testid="banner-impersonation">
      <div className="flex items-center gap-2 text-amber-800 text-sm font-medium">
        <Shield className="w-4 h-4 text-amber-600" />
        Viewing as admin of <strong>{orgName}</strong>
      </div>
      <Button size="sm" variant="outline" className="border-amber-400 text-amber-800 hover:bg-amber-100" onClick={handleStop} disabled={stopping} data-testid="button-stop-impersonation">
        <LogOut className="w-3.5 h-3.5 mr-1.5" />
        {stopping ? "Leaving..." : "Back to Platform"}
      </Button>
    </div>
  );
}

export default function AdminLayout({ children }: { children: React.ReactNode }) {
  const [location] = useLocation();
  const { logout, user: authUser } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [feedbackOpen, setFeedbackOpen] = useState(false);

  const { data: features } = useQuery<{ featureKey: string; enabled: boolean }[]>({
    queryKey: ["/api/admin/features"],
  });

  const { data: crmStats } = useQuery<{ totalUnread?: number }>({
    queryKey: ["/api/admin/crm/stats"],
    refetchInterval: 30000,
  });

  const { data: orgSettings } = useQuery<{ businessMode?: string }>({
    queryKey: ["/api/admin/org-settings"],
  });

  const enabledKeys = useMemo(() => {
    if (!features) return null;
    const keys = new Set<string>();
    for (const f of features) {
      if (f.enabled) keys.add(f.featureKey);
    }
    return keys;
  }, [features]);

  const userDisabledFeatures = useMemo(() => {
    const disabled = (authUser as any)?.disabledFeatures;
    return new Set<string>(Array.isArray(disabled) ? disabled : []);
  }, [authUser]);

  // Bookings stays visible for ALL business modes — service_business orgs need
  // it to validate cleaning jobs against reservations. (Was previously hidden
  // for the typo'd "services_business" value, which never matched anything.)
  const visibleItems = useMemo(() => {
    return navItems.filter(item => {
      if (!item.featureKey) return true;
      if (userDisabledFeatures.has(item.featureKey)) return false;
      if (!enabledKeys) return true;
      return enabledKeys.has(item.featureKey);
    });
  }, [enabledKeys, userDisabledFeatures]);

  const showNotifications = (!enabledKeys || enabledKeys.has("notifications_push")) && !userDisabledFeatures.has("notifications_push");
  const showLumi = (!enabledKeys || enabledKeys.has("lumi_ai")) && !userDisabledFeatures.has("lumi_ai");

  const groupedItems = useMemo(() => {
    const groups: { section: string | null; items: NavItem[] }[] = [];
    let currentSection: string | null = null;
    let currentGroup: NavItem[] = [];
    for (const item of visibleItems) {
      const section = item.section || null;
      if (section !== currentSection) {
        if (currentGroup.length > 0) {
          groups.push({ section: currentSection, items: currentGroup });
        }
        currentSection = section;
        currentGroup = [item];
      } else {
        currentGroup.push(item);
      }
    }
    if (currentGroup.length > 0) {
      groups.push({ section: currentSection, items: currentGroup });
    }
    return groups;
  }, [visibleItems]);

  const activeSection = useMemo(() => {
    for (const item of visibleItems) {
      if (location === item.href || (item.href !== '/admin' && location.startsWith(item.href))) {
        return item.section || null;
      }
    }
    return null;
  }, [visibleItems, location]);

  const [collapsedSections, setCollapsedSections] = useState<Set<string>>(() => {
    try {
      const saved = localStorage.getItem("staio-sidebar-collapsed");
      if (saved) return new Set(JSON.parse(saved));
    } catch {}
    return new Set();
  });

  const toggleSection = useCallback((section: string) => {
    setCollapsedSections(prev => {
      const next = new Set(prev);
      if (next.has(section)) {
        next.delete(section);
      } else {
        next.add(section);
      }
      // Array.from avoids --downlevelIteration requirement for Set spread (BUG-T5)
      try { localStorage.setItem("staio-sidebar-collapsed", JSON.stringify(Array.from(next))); } catch {}
      return next;
    });
  }, []);

  const isActive = (href: string) => location === href || (href !== '/admin' && location.startsWith(href));

  const NavContent = ({ dark = false }: { dark?: boolean }) => (
    <nav className="space-y-0.5">
      {groupedItems.map((group, gi) => {
        const isCollapsed = group.section ? collapsedSections.has(group.section) : false;
        const hasActiveItem = group.items.some(item => isActive(item.href));

        return (
          <div key={gi}>
            {group.section ? (
              <button
                type="button"
                onClick={() => toggleSection(group.section!)}
                className={`w-full flex items-center justify-between text-[10px] uppercase tracking-wider font-semibold px-3 pt-4 pb-1 transition-colors ${dark ? "text-white/30 hover:text-white/50" : "text-muted-foreground/60 hover:text-muted-foreground"}`}
                data-testid={`section-${group.section!.toLowerCase()}`}
              >
                <span className={hasActiveItem && isCollapsed ? (dark ? "text-amber-400" : "text-primary") : ""}>{group.section}</span>
                <ChevronDown className={`h-3 w-3 transition-transform duration-200 ${isCollapsed ? '-rotate-90' : ''}`} />
              </button>
            ) : null}
            {!isCollapsed && group.items.map((item) => {
              const active = isActive(item.href);
              return (
                <Link key={item.href} href={item.href}>
                  <div
                    className={`flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-all cursor-pointer ${
                      dark
                        ? active
                          ? "bg-white/10 text-white"
                          : "text-white/60 hover:bg-white/5 hover:text-white/80"
                        : active
                          ? "bg-primary/10 text-primary"
                          : "text-muted-foreground hover:bg-secondary hover:text-foreground"
                    }`}
                    data-testid={item.testId}
                  >
                    <item.icon className="h-4 w-4" />
                    <span className="flex-1">{item.title}</span>
                    {item.href === "/admin/crm" && (crmStats?.totalUnread || 0) > 0 && (
                      <span className="ml-auto min-w-[18px] h-[18px] flex items-center justify-center rounded-full bg-red-500 text-white text-[10px] font-bold px-1">
                        {crmStats!.totalUnread! > 99 ? "99+" : crmStats!.totalUnread}
                      </span>
                    )}
                  </div>
                </Link>
              );
            })}
          </div>
        );
      })}
    </nav>
  );

  const bottomNavItems = useMemo(() => {
    const priorities = [
      { href: "/admin", icon: LayoutDashboard, label: "Home" },
      { href: "/admin/reservations", icon: CalendarDays, label: "Bookings" },
      { href: "/admin/cleaning-jobs", icon: Sparkles, label: "Cleaning" },
      { href: "/admin/finances", icon: DollarSign, label: "Finance" },
    ];
    return priorities.filter(item => {
      const navItem = navItems.find(n => n.href === item.href);
      if (!navItem?.featureKey) return true;
      if (userDisabledFeatures.has(navItem.featureKey)) return false;
      return !enabledKeys || enabledKeys.has(navItem.featureKey);
    });
  }, [enabledKeys, userDisabledFeatures]);

  return (
    <div className="h-screen flex overflow-hidden" style={{ background: "linear-gradient(135deg, hsl(30 33% 98%) 0%, hsl(30 20% 96%) 100%)" }}>
      <aside className="hidden w-64 lg:flex flex-col shrink-0" style={{ background: "linear-gradient(180deg, #3D2B1F 0%, #2A1D14 100%)" }}>
        <div className="px-5 py-5 flex items-center gap-3 shrink-0" style={{ borderBottom: "1px solid rgba(255,255,255,0.08)" }}>
          <img src={staioIcon} alt="Staio" className="w-8 h-8 rounded-lg" />
          <span className="font-display font-bold text-lg text-white">Staio</span>
        </div>
        <div className="flex-1 px-3 py-3 overflow-y-auto min-h-0 no-scrollbar">
          <NavContent dark />
        </div>
        <div className="px-3 pb-4 shrink-0" style={{ borderTop: "1px solid rgba(255,255,255,0.08)" }}>
          <div className="pt-3 space-y-1">
            {showLumi && (
              <button
                onClick={() => window.dispatchEvent(new CustomEvent("open-lumi"))}
                className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium text-amber-300/80 hover:text-amber-300 hover:bg-amber-500/10 transition-colors"
                data-testid="button-lumi-sidebar"
              >
                <Lightbulb className="h-4 w-4" />
                Ask Lumi
              </button>
            )}
            <button
              onClick={() => setFeedbackOpen(true)}
              className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium text-white/50 hover:text-white/80 hover:bg-white/5 transition-colors"
              data-testid="button-send-feedback"
            >
              <MessageSquarePlus className="h-4 w-4" />
              Send Feedback
            </button>
            <Link href="/worker">
              <button
                className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium text-white/50 hover:text-white/80 hover:bg-white/5 transition-colors"
                data-testid="button-worker-view"
              >
                <Briefcase className="h-4 w-4" />
                Worker View
              </button>
            </Link>
            <button
              onClick={() => logout()}
              className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium text-red-400/80 hover:text-red-400 hover:bg-red-500/10 transition-colors"
              data-testid="button-logout"
            >
              <LogOut className="h-4 w-4" />
              Sign Out
            </button>
          </div>
        </div>
      </aside>

      <div className="flex-1 flex flex-col min-w-0 min-h-0">
        <header className="h-14 border-b bg-white/80 dark:bg-card/80 backdrop-blur-md flex items-center px-4 justify-between gap-2 shrink-0">
          <div className="flex items-center gap-2 lg:hidden">
            <img src={staioIcon} alt="Staio" className="w-8 h-8 rounded-lg" />
            <span className="font-display font-bold text-lg">Staio</span>
          </div>
          <div className="hidden lg:block" />
          <div className="flex items-center gap-1">
            {showNotifications && <NotificationBell />}
            <div className="lg:hidden">
              <Sheet open={isOpen} onOpenChange={setIsOpen}>
                <SheetTrigger asChild>
                  <Button variant="ghost" size="icon" data-testid="button-mobile-menu">
                    <Menu className="h-6 w-6" />
                  </Button>
                </SheetTrigger>
                <SheetContent side="left" className="w-64 p-0 flex flex-col h-full" style={{ background: "linear-gradient(180deg, #3D2B1F 0%, #2A1D14 100%)" }}>
                  <div className="px-5 py-5 shrink-0" style={{ borderBottom: "1px solid rgba(255,255,255,0.08)" }}>
                    <div className="flex items-center gap-3">
                      <img src={staioIcon} alt="Staio" className="w-8 h-8 rounded-lg" />
                      <span className="font-display font-bold text-lg text-white">Staio</span>
                    </div>
                  </div>
                  <div className="flex-1 px-3 py-3 overflow-y-auto no-scrollbar">
                    <NavContent dark />
                  </div>
                  <div className="px-3 pb-4 shrink-0" style={{ borderTop: "1px solid rgba(255,255,255,0.08)" }}>
                    <div className="pt-3 space-y-1">
                      {showLumi && (
                        <button
                          onClick={() => { setIsOpen(false); window.dispatchEvent(new CustomEvent("open-lumi")); }}
                          className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium text-amber-300/80 hover:text-amber-300 hover:bg-amber-500/10 transition-colors"
                        >
                          <Lightbulb className="h-4 w-4" />
                          Ask Lumi
                        </button>
                      )}
                      <button
                        onClick={() => { setIsOpen(false); setFeedbackOpen(true); }}
                        className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium text-white/50 hover:text-white/80 hover:bg-white/5 transition-colors"
                      >
                        <MessageSquarePlus className="h-4 w-4" />
                        Send Feedback
                      </button>
                      <Link href="/worker">
                        <button
                          onClick={() => setIsOpen(false)}
                          className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium text-white/50 hover:text-white/80 hover:bg-white/5 transition-colors"
                        >
                          <Briefcase className="h-4 w-4" />
                          Worker View
                        </button>
                      </Link>
                      <button
                        onClick={() => logout()}
                        className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium text-red-400/80 hover:text-red-400 hover:bg-red-500/10 transition-colors"
                      >
                        <LogOut className="h-4 w-4" />
                        Sign Out
                      </button>
                    </div>
                  </div>
                </SheetContent>
              </Sheet>
            </div>
          </div>
        </header>

        {(authUser as any)?.impersonatingAsOrg && (
          <ImpersonationBanner orgName={(authUser as any)?.orgName} />
        )}
        <main className="flex-1 p-4 lg:p-8 overflow-y-auto overflow-x-hidden min-h-0 pb-20 lg:pb-8">
          {children}
        </main>
      </div>

      <nav className="lg:hidden fixed bottom-0 left-0 right-0 z-50 bg-white/95 dark:bg-card/95 backdrop-blur-md border-t border-border/50 safe-area-bottom" data-testid="mobile-bottom-nav">
        <div className="flex items-center justify-around h-16 px-1">
          {bottomNavItems.map((item) => {
            const active = isActive(item.href);
            return (
              <Link key={item.href} href={item.href}>
                <button
                  className={`flex flex-col items-center justify-center gap-0.5 min-w-[56px] py-1 px-2 rounded-lg transition-colors ${
                    active
                      ? "text-primary"
                      : "text-muted-foreground"
                  }`}
                  data-testid={`bottom-nav-${item.label.toLowerCase()}`}
                >
                  <item.icon className={`h-5 w-5 ${active ? "text-primary" : ""}`} />
                  <span className={`text-[10px] font-medium ${active ? "text-primary" : ""}`}>{item.label}</span>
                </button>
              </Link>
            );
          })}
          <button
            onClick={() => setIsOpen(true)}
            className="flex flex-col items-center justify-center gap-0.5 min-w-[56px] py-1 px-2 rounded-lg text-muted-foreground transition-colors"
            data-testid="bottom-nav-more"
          >
            <Menu className="h-5 w-5" />
            <span className="text-[10px] font-medium">More</span>
          </button>
        </div>
      </nav>

      {showLumi && <LumiGlobal />}
      {showLumi && <LumiCommand />}
      <FeedbackDialog open={feedbackOpen} onOpenChange={setFeedbackOpen} />
    </div>
  );
}
